import { useCallback, useEffect, useRef, useState } from 'react';

import { castArray, compact, flatten, without } from 'lodash';

function toCallbackArray(v: unknown): (() => void)[] {
    return compact(flatten(castArray(v))) as (() => void)[];
}

/**
 * Encapsulates Aiko (HubSpot Conversations) widget lifecycle:
 * - Opens widget on demand via onNeedSupportClick (load + open, or queue via hsConversationsOnReady)
 * - Removes widget when closed so it stays hidden until next click
 */
export function useAikoSupport(options?: { onBeforeOpen?: () => void }) {
    const { onBeforeOpen } = options ?? {};

    const [isAikoOpen, setIsAikoOpen] = useState(false);
    const widgetClosedRegisteredRef = useRef(false);
    const queuedOnReadyRef = useRef<(() => void) | null>(null);

    const handleWidgetClosed = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.HubSpotConversations?.widget?.remove?.();
        }
        setIsAikoOpen(false);
    }, []);

    const registerWidgetClosedListener = useCallback(() => {
        if (
            typeof window !== 'undefined' &&
            window.HubSpotConversations &&
            !widgetClosedRegisteredRef.current
        ) {
            window.HubSpotConversations.on('widgetClosed', handleWidgetClosed);
            widgetClosedRegisteredRef.current = true;
        }
    }, [handleWidgetClosed]);

    const onNeedSupportClick = useCallback(() => {
        onBeforeOpen?.();

        const openWidget = () => {
            if (typeof window === 'undefined' || !window.HubSpotConversations?.widget) return;
            registerWidgetClosedListener();
            window.HubSpotConversations.widget.load();
            window.HubSpotConversations.widget.open();
            setIsAikoOpen(true);
        };

        if (typeof window === 'undefined') return;

        if (window.HubSpotConversations?.widget) {
            openWidget();
        } else {
            const onReady = () => openWidget();
            const previous = queuedOnReadyRef.current;
            const arr = previous
                ? without(toCallbackArray(window.hsConversationsOnReady), previous)
                : toCallbackArray(window.hsConversationsOnReady);
            queuedOnReadyRef.current = onReady;
            window.hsConversationsOnReady = [...arr, onReady];
        }
    }, [onBeforeOpen, registerWidgetClosedListener]);

    useEffect(() => {
        registerWidgetClosedListener();
        return () => {
            if (
                typeof window !== 'undefined' &&
                window.HubSpotConversations &&
                widgetClosedRegisteredRef.current
            ) {
                window.HubSpotConversations.off('widgetClosed', handleWidgetClosed);
                widgetClosedRegisteredRef.current = false;
            }
        };
    }, [handleWidgetClosed, registerWidgetClosedListener]);

    useEffect(() => {
        return () => {
            if (typeof window === 'undefined') return;
            const ours = queuedOnReadyRef.current;
            if (!ours) return;
            window.hsConversationsOnReady = without(
                toCallbackArray(window.hsConversationsOnReady),
                ours
            );
            queuedOnReadyRef.current = null;
        };
    }, []);

    return { onNeedSupportClick, isAikoOpen };
}
